<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
// 십이운성 / 숨겨진 강점
import { VCol, VRow } from 'vuetify/lib/components/index.mjs';
import fiveMixin from '@/composables/fiveMixin';
import { splitFirstSentence, splitBySentence, transformMarkedText } from '@/composables/splitSentence';
import SajuEightTxt from './SajuEightTxt.vue';
import BasicContentsTitle from './BasicContentsTitle.vue';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const props = defineProps({
  ratingResult: Number,
  ratingStatus: Boolean,
});
const emit = defineEmits(['postRate', 'goToOverView']);

const { transdata, birthData, birthJijanggan, curDaeun, sajuIlgan, sData } =
  storeToRefs(useAnalysisDataStore());

const langMode = ref('CHNKOR');
const birthTitle = ref('시일월년');
// 일간에 active
const selectedData = ref(null);
const isActive = ref(false);

const { ...five } = fiveMixin();

// 십이운성
const 십이운성특성 = computed(() => sData.value?.['십이운성특성']);
const 십이운성기본특성 = computed(() => splitFirstSentence(십이운성특성.value?.기본특성));
const 십이운성핵심문장 = computed(() => transformMarkedText(십이운성기본특성.value.first));
const 십이운성설명 = computed(() => 십이운성기본특성.value.restSentences.map(transformMarkedText));
const 십이운성상세특성 = computed(() => splitBySentence(십이운성특성.value?.상세특성).map(transformMarkedText));
const 십이운성사주위치 = computed(() => 십이운성특성.value?.사주위치);
const 십이운성풀이대상 = computed(() => 십이운성특성.value?.풀이대상);

// 십이신살 (숨겨진 강점)
const 십이신살특성 = computed(() => sData.value?.['십이신살특성']);
const 십이신살기본특성 = computed(() => splitFirstSentence(십이신살특성.value?.기본특성));
const 십이신살핵심문장 = computed(() => transformMarkedText(십이신살기본특성.value.first));
const 십이신살설명 = computed(() => 십이신살기본특성.value.restSentences.map(transformMarkedText));
const 십이신살상세특성 = computed(() => splitBySentence(십이신살특성.value?.상세특성).map(transformMarkedText));
const 십이신살사주위치 = computed(() => 십이신살특성.value?.사주위치);
const 십이신살풀이대상 = computed(() => 십이신살특성.value?.풀이대상);
</script>

<template>
  <VRow class="ss-full-height">
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card right">
        <BasicContentsTitle
          number="6"
          text="<span>나의 성향과 스타일&nbsp;</span><span>(십이운성으로 보는 나)</span>"
        />
        <section class="summ-body">
          <div class="summ-body__saju data-sec-01-box">
            <SajuEightTxt
              ref="ManseBirthMainArea"
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-transdata="transdata"
              :pr-type="'birth'"
              :pr-cur-daeun="curDaeun"
              :selected-data="selectedData"
              :is-active="isActive"
              :active-page="'page3'"
              :pr-position="십이운성사주위치"
              :pr-target="십이운성풀이대상"
            />
          </div>
          <div class="summ-body__result-analysis">
            <div class="key-sentence ganji-txt" :class="[five.getColorFive(sajuIlgan)]" v-html="십이운성핵심문장" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">나의 성향과 표현 방식</div>
              <div class="result-analysis__desc" v-for="(line, index) in 십이운성설명" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">나의 반응과 성장</div>
              <div class="result-analysis__desc" v-for="(line, index) in 십이운성상세특성" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
          </div>
          <div class="summ-body__result-img"></div>
        </section>
      </div>
    </VCol>
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card">
        <BasicContentsTitle
          number="7"
          text="<span>숨겨진 강점&nbsp;</span><span>(신살, 오행의 우위)</span>"
        />
        <section class="summ-body">
          <div class="summ-body__saju data-sec-01-box">
            <SajuEightTxt
              ref="ManseBirthMainArea"
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-transdata="transdata"
              :pr-type="'birth'"
              :pr-cur-daeun="curDaeun"
              :selected-data="selectedData"
              :is-active="isActive"
              :active-page="'page4'"
              :pr-position="십이신살사주위치"
              :pr-target="십이신살풀이대상"
            />
          </div>
          <div class="summ-body__result-analysis">
            <div class="key-sentence ganji-txt" :class="[five.getColorFive(sajuIlgan)]" v-html="십이신살핵심문장" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">조용히 빛나는 나의 가능성</div>
              <div class="result-analysis__desc-wrap">
                <div class="result-analysis__desc" v-for="(line, index) in 십이신살설명" :key="index">
                  <span v-html="line"></span>
                </div>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">숨겨진 듯, 그러나 언제나 작동하는 힘</div>
              <div class="result-analysis__desc" v-for="(line, index) in 십이신살상세특성" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
          </div>
          <div class="summ-body__result-img"></div>
        </section>
      </div>
      <!-- SurveyCard 영역 - 주석 처리
      <div class="summ-card">
        <section class="summ-body rating">
          <SurveyCard
            style="width: 100%"
            :ratingResult="ratingResult"
            :ratingStatus="ratingStatus"
            @postRate="postRate"
          />
          <div class="summary-replay">
            <button class="replay-btn content_review_button_restart" @click="goToCoverPage()">
              <i class="return-icon"></i>
              다시보기
            </button>
          </div>
        </section>
      </div>
      -->
    </VCol>
  </VRow>
</template>
